import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import IconButton from "@mui/material/IconButton";
import { FaTimes } from "react-icons/fa";
import axios from "axios";
import BtnLoader from "Components/Common/Loader/BtnLoader";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 400,
  bgcolor: "background.paper",
  border: "none",
  borderRadius: 8,
  boxShadow: 24,
  p: 4,
};

const EmojiEditPopup = ({
  editEmojiModalOpen,
  handleEmojiModalClose,
  emojiDetails,
  fetchAllEmojis,
  staffId,
  categories,
}) => {
  const [formData, setFormData] = useState({
    emoji: "",
    emojiName: "",
    emojiPrice: "",
    category: "", // ✅ added
    isActive: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Update `formData` whenever `emojiDetails` changes
  useEffect(() => {
    if (emojiDetails) {
      setFormData({
        emoji: emojiDetails?.emoji || "",
        emojiName: emojiDetails?.name || "",
        emojiPrice: emojiDetails?.coinDeduct || "",
        // ✅ resolve category whether it's a populated object or a raw id string
        category: emojiDetails?.category?._id || emojiDetails?.category || "",
        isActive: emojiDetails?.isActive ?? true,
      });
    }
  }, [emojiDetails]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prevState) => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetFormData = () => {
    setFormData({
      emoji: emojiDetails?.emoji || "",
      emojiName: emojiDetails?.name || "",
      emojiPrice: emojiDetails?.coinDeduct || "",
      // ✅ same resolution here so reset stays consistent
      category: emojiDetails?.category?._id || emojiDetails?.category || "",
      isActive: emojiDetails?.isActive ?? true,
    });
  };

  const handleModalClose = () => {
    resetFormData();
    setErrorMessage("");
    handleEmojiModalClose();
  };

  const createStaffActivityLog = async (actionType, description, staffId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/staffs-activity-log/create-staffs-activity`,
        {
          staff: staffId,
          action: actionType,
          description,
        }
      );
    } catch (error) {
      console.error("Activity log failed:", error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");

    const data = JSON.stringify({
      emoji: formData.emoji,
      name: formData.emojiName,
      description: "All Happy emoji",
      coinDeduct: formData.emojiPrice,
      category: formData.category, // ✅ now included in the payload
      isActive: formData.isActive,
    });

    const config = {
      method: "put",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_API_URL}/emoji-package/update-emoji-packages/${emojiDetails?._id}`,
      headers: {
        "Content-Type": "application/json",
      },
      data: data,
    };

    try {
      await axios.request(config);

      // 🔥 ACTIVITY LOG
      await createStaffActivityLog(
        "UPDATE_EMOJI",
        `Emoji Gift Updated:
     Name: ${formData.emojiName} |
     Emoji: ${formData.emoji} |
     Price: ₹${formData.emojiPrice}`,
        staffId
      );

      fetchAllEmojis();
      handleModalClose();
    } catch (error) {
      console.error("Error:", error);
      setErrorMessage("Failed to update the emoji package. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={editEmojiModalOpen}
      onClose={handleModalClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <IconButton
          aria-label="close"
          onClick={handleModalClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "#c51369",
          }}
        >
          <FaTimes />
        </IconButton>
        <div className="mx-auto">
          <form onSubmit={handleSubmit} className="space-y-4">
            <h2 className="mb-4 text-center text-2xl font-bold">
              Edit Emoji Gift
            </h2>

            <div className="mb-4">
              <label
                htmlFor="emoji"
                className="mb-2 block text-lg tracking-wide text-navy-700"
              >
                Emoji in text format
              </label>
              <input
                type="text"
                id="emoji"
                name="emoji"
                placeholder='Enter this "🥰" type emoji format'
                value={formData?.emoji}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
                required
              />
            </div>

            <div className="mb-4">
              <label
                htmlFor="emojiName"
                className="mb-2 block text-lg tracking-wide text-navy-700"
              >
                Emoji Name
              </label>
              <input
                type="text"
                id="emojiName"
                name="emojiName"
                value={formData?.emojiName}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
                required
              />
            </div>

            <div className="mb-4">
              <label
                htmlFor="emojiPrice"
                className="mb-2 block text-lg tracking-wide text-navy-700"
              >
                Emoji Price
              </label>
              <input
                type="text"
                id="emojiPrice"
                name="emojiPrice"
                value={formData?.emojiPrice}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
                required
              />
            </div>

            <div className="mb-4">
              <label className="mb-2 block text-lg tracking-wide text-navy-700">
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
                required
              >
                <option value="">Select Category</option>
                {categories.map((cat) => (
                  <option key={cat._id} value={cat._id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4 flex items-center justify-between">
              <label className="text-lg tracking-wide text-navy-700">
                Active Status
              </label>

              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                className="h-5 w-5 cursor-pointer"
              />
            </div>

            {errorMessage && (
              <p className="mb-4 text-center text-red-500">{errorMessage}</p>
            )}

            {isLoading ? (
              <button className="flex w-full items-center justify-center rounded-md bg-[#c51369] p-2">
                <BtnLoader />
              </button>
            ) : (
              <button
                type="submit"
                className="w-full rounded-md bg-[#c51369] p-2 text-white transition duration-200 hover:bg-[#a10f5b]"
              >
                Submit
              </button>
            )}
          </form>
        </div>
      </Box>
    </Modal>
  );
};

export default EmojiEditPopup;
